/**
 * Unified Current Organization Hook
 *
 * Provides a single source of truth for current organization management using TanStack Query.
 * Replaces the dual-state pattern of OrganizationContext + TanStack Query.
 *
 * Features:
 * - Current organization stored in localStorage
 * - TanStack Query as single source of truth
 * - Optimistic updates for instant UI feedback
 * - Automatic prefetching on organization switch
 * - Type-safe organization operations
 *
 * @version 1.0.0
 * @last-updated 2026-02-05
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { organizationService } from "@/services/core/organization.service";
import { organizationKeys } from "@/hooks/useOrganizations";
import { queryKeys } from "@/lib/queryClient";
import { toast } from '@/components/ui/sonner';
import type { Organization } from "@/types/organization";
import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const CURRENT_ORG_STORAGE_KEY = "flowstate_current_org_id";
export const LAST_TEAM_ORG_STORAGE_KEY = "flowstate_last_team_org_id";

/**
 * Shared classifier for personal organization
 */
export function isPersonalOrganization(org?: { org_type?: string; slug?: string } | null): boolean {
  if (!org) return false;
  if (org.slug && !org.slug.startsWith('personal-')) {
    return false;
  }
  return org.org_type === 'personal' || (!!org.slug && org.slug.startsWith('personal-'));
}

/**
 * Find the personal organization from a list of organizations
 */
export function getPersonalOrganization(organizations: Organization[]): Organization | null {
  return organizations.find((org) => isPersonalOrganization(org)) ?? null;
}

/**
 * Get the last active non-personal team organization from a list of organizations
 */
export function getLastTeamOrganization(organizations: Organization[]): Organization | null {
  const teamOrgs = organizations.filter((org) => !isPersonalOrganization(org));
  if (teamOrgs.length === 0) return null;

  try {
    const lastTeamId = localStorage.getItem(LAST_TEAM_ORG_STORAGE_KEY);
    if (lastTeamId) {
      const match = teamOrgs.find((org) => org.id === lastTeamId);
      if (match) return match;
    }
  } catch (error) {
    logger.error("Error reading last team org from localStorage:", { error });
  }

  return teamOrgs[0] ?? null;
}

/** Stable context so hook order stays consistent (avoids React "order of Hooks" mismatch after HMR). */
const HookOrderStabilityContext = createContext<null>(null);

/**
 * Hook return interface
 */
export interface UseCurrentOrganizationReturn {
  /** Current active organization */
  currentOrganization: Organization | null;
  /** All organizations user belongs to */
  organizations: Organization[];
  /** Loading state for organizations list */
  isLoading: boolean;
  /** Error from organizations query */
  error: Error | null;
  /** Switch to a different organization */
  switchOrganization: (orgId: string) => Promise<void>;
  /** Switch specifically to personal workspace */
  switchToPersonalWorkspace: () => Promise<void>;
  /** Switch specifically to primary/last team workspace */
  switchToTeamWorkspace: () => Promise<void>;
  /** Is a switch operation in progress */
  isSwitching: boolean;
  /** Refresh organizations list */
  refreshOrganizations: () => Promise<void>;
}

/**
 * Get current organization ID from localStorage
 */
export function readPersistedOrgId(): string | null {
  try {
    return localStorage.getItem(CURRENT_ORG_STORAGE_KEY);
  } catch (error) {
    logger.error("Error reading from localStorage:", { error: error });
    return null;
  }
}

/**
 * Persist org id to localStorage and broadcast to every useCurrentOrganization() subscriber.
 */
export function publishCurrentOrgId(
  queryClient: QueryClient,
  orgId: string | null,
  persist = true,
): void {
  if (persist) {
    try {
      if (orgId) {
        localStorage.setItem(CURRENT_ORG_STORAGE_KEY, orgId);
      } else {
        localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
      }
    } catch (error) {
      logger.error("Error writing to localStorage:", { error: error });
    }
  }
  queryClient.setQueryData(queryKeys.currentOrganization.id(), orgId);
}

/**
 * Unified hook for current organization management
 *
 * Uses TanStack Query as the single source of truth for organization state.
 * LocalStorage is used only for persistence across sessions.
 */
export function useCurrentOrganization(): UseCurrentOrganizationReturn {
  // Stable leading hook so the 7th hook in callers (e.g. TaskDetailModal via useSubtasks) stays useContext and does not flip to useState after HMR.
  useContext(HookOrderStabilityContext);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Shared across every hook instance — switching org in the navbar updates page queries immediately.
  const { data: currentOrgId = null } = useQuery({
    queryKey: queryKeys.currentOrganization.id(),
    queryFn: readPersistedOrgId,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: readPersistedOrgId,
  });

  // Fetch all user organizations
  const {
    data: organizations = [],
    isLoading,
    error,
    refetch: refreshOrganizations,
  } = useQuery({
    queryKey: organizationKeys.lists(),
    queryFn: async () => {
      const response = await organizationService.getUserOrganizations();
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch organizations");
      }
      return response.data || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Safety net: if user is authenticated but has zero organizations,
  // call ensure_user_has_org RPC to create a personal workspace.
  const ensureOrgAttempted = useRef(false);

  useEffect(() => {
    if (
      !isLoading &&
      organizations.length === 0 &&
      user?.id &&
      !ensureOrgAttempted.current
    ) {
      ensureOrgAttempted.current = true;
      supabase
        .rpc('ensure_user_has_org', { p_user_id: user.id })
        .then(({ error: rpcError }) => {
          if (rpcError) {
            logger.error('Failed to ensure personal org:', { error: rpcError });
            return;
          }
          queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
        });
    }
  }, [isLoading, organizations.length, user?.id, queryClient]);

  // Default to first org when nothing stored or stored id is invalid.
  useEffect(() => {
    if (!organizations.length) return;
    if (currentOrgId && organizations.some((org) => org.id === currentOrgId)) {
      return;
    }
    const firstOrg = organizations[0];
    if (firstOrg) {
      publishCurrentOrgId(queryClient, firstOrg.id);
    }
  }, [organizations, currentOrgId, queryClient]);

  const currentOrganization = useMemo(() => {
    if (!organizations.length) return null;
    if (currentOrgId) {
      const stored = organizations.find((org) => org.id === currentOrgId);
      if (stored) return stored;
    }
    return organizations[0] ?? null;
  }, [organizations, currentOrgId]);

  // Track last non-personal team org whenever active org changes
  useEffect(() => {
    if (currentOrganization && !isPersonalOrganization(currentOrganization)) {
      try {
        localStorage.setItem(LAST_TEAM_ORG_STORAGE_KEY, currentOrganization.id);
      } catch (e) {
        logger.error("Error writing last team org to localStorage:", { error: e });
      }
    }
  }, [currentOrganization]);

  // Switch organization mutation with optimistic updates
  const switchMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const org = organizations.find((o) => o.id === orgId);
      if (!org) {
        throw new Error("Organization not found or you don't have access");
      }
      return org;
    },
    onMutate: async (orgId) => {
      await queryClient.cancelQueries({ queryKey: organizationKeys.all });
      const previousOrgId = currentOrgId;
      publishCurrentOrgId(queryClient, orgId);
      return { previousOrgId };
    },
    onError: (err, _orgId, context) => {
      if (context?.previousOrgId !== undefined) {
        publishCurrentOrgId(queryClient, context.previousOrgId);
      }
      toast.error("Failed to switch organization", {
        description: err instanceof Error ? err.message : "Unknown error occurred",
      });
    },
    onSuccess: (org) => {
      if (!isPersonalOrganization(org)) {
        try {
          localStorage.setItem(LAST_TEAM_ORG_STORAGE_KEY, org.id);
        } catch (e) {
          logger.error("Error writing last team org to localStorage:", { error: e });
        }
      }

      // Org-scoped queries include organizationId in their key — they refetch when it changes.
      queryClient.prefetchQuery({
        queryKey: organizationKeys.detail(org.id),
        queryFn: async () => {
          const response = await organizationService.getOrganizationById(org.id);
          return response.data;
        },
      });

      toast.success("Organization switched", { description: `Now viewing ${org.name}` });
    },
  });

  // Cross-tab sync: storage events fire only in other tabs, not the active one.
  useEffect(() => {
    const handleStorageSync = (e: StorageEvent) => {
      if (e.key === CURRENT_ORG_STORAGE_KEY && e.newValue) {
        queryClient.setQueryData(queryKeys.currentOrganization.id(), e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageSync);
    return () => window.removeEventListener('storage', handleStorageSync);
  }, [queryClient]);

  const switchOrganization = async (orgId: string) => {
    await switchMutation.mutateAsync(orgId);
  };

  const switchToPersonalWorkspace = async () => {
    const personalOrg = getPersonalOrganization(organizations);
    if (personalOrg && personalOrg.id !== currentOrgId) {
      await switchOrganization(personalOrg.id);
    }
  };

  const switchToTeamWorkspace = async () => {
    const teamOrg = getLastTeamOrganization(organizations);
    if (teamOrg && teamOrg.id !== currentOrgId) {
      await switchOrganization(teamOrg.id);
    }
  };

  return {
    currentOrganization,
    organizations,
    isLoading,
    error: error instanceof Error ? error : null,
    switchOrganization,
    switchToPersonalWorkspace,
    switchToTeamWorkspace,
    isSwitching: switchMutation.isPending,
    refreshOrganizations: async () => {
      await refreshOrganizations();
    },
  };
}

export function useIsCurrentOrganization(organizationId: string): boolean {
  const { currentOrganization } = useCurrentOrganization();
  return currentOrganization?.id === organizationId;
}

export function useCurrentOrganizationId(): string | null {
  const { currentOrganization } = useCurrentOrganization();
  return currentOrganization?.id || null;
}
